import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from './auth';
import { useOffline } from './offlineContext';
import { CACHE_RULES, appointmentsExpiry, type CacheKey } from '../offline/policy';

/**
 * `useQuery`, plus a copy on the device for the screens that have earned one.
 *
 * Network first, always. The cached copy is a fallback for when the network
 * fails, never a substitute for asking — a worker whose certificate was
 * revoked this morning must not be shown yesterday's answer because it was
 * faster to reach.
 *
 * The three states a screen has to distinguish:
 *
 *   · fresh          — came from the server just now
 *   · stale          — from the device, with the date it was saved
 *   · nothing at all — no network and no copy, which is a real state and
 *                      needs its own words rather than an endless spinner
 *
 * `stale` is surfaced rather than hidden. A worker holding up a phone at a
 * gate deserves to know whether they are showing today's answer or a saved
 * one, and pretending there is no difference is how an offline app becomes
 * untrustworthy.
 */

export interface OfflineQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** True when `data` came from the device rather than the server. */
  stale: boolean;
  /** When the cached copy was saved. Null unless `stale`. */
  cachedAt: Date | null;
  reload: () => void;
}

function expiryFor(key: CacheKey, value: unknown): number {
  // Appointments expire with the appointment, not on a fixed clock: an address
  // for a slot that has passed is clutter presented as a fallback.
  if (key === 'appointments' && Array.isArray(value)) {
    return appointmentsExpiry(value as Array<{ scheduledFor?: string | null }>);
  }
  return Date.now() + CACHE_RULES[key].ttlMs;
}

export function useOfflineQuery<T>(
  key: CacheKey,
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): OfflineQueryResult<T> {
  const { store } = useOffline();

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stale, setStale] = useState(false);
  const [cachedAt, setCachedAt] = useState<Date | null>(null);
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);

    (async () => {
      const cacheKey = CACHE_RULES[key].key;

      try {
        const fresh = await fetcher();
        if (!live) return;
        setData(fresh);
        setStale(false);
        setCachedAt(null);
        setError(null);
        // Written after the render, not before it — a slow write must not
        // delay showing the worker what they asked for.
        void store?.write(cacheKey, fresh, expiryFor(key, fresh));
      } catch (e) {
        if (!live) return;

        const cached = await store?.read<T>(cacheKey);
        if (!live) return;

        if (cached) {
          // The saved copy answers the question; the network failure is not
          // news at that point, so it is not shown as an error.
          setData(cached.value);
          setStale(true);
          setCachedAt(new Date(cached.storedAt));
          setError(null);
        } else {
          setData(null);
          setStale(false);
          setCachedAt(null);
          setError(errorMessage(e));
        }
      } finally {
        if (live) setLoading(false);
      }
    })();

    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, store]);

  return { data, loading, error, stale, cachedAt, reload };
}

/**
 * How long ago, in words a worker reads rather than a timestamp they decode.
 * "Saved 3 days ago" tells them how much to trust it; "2026-09-01T04:12:00Z"
 * does not.
 */
export function savedAgo(when: Date, now = new Date()): string {
  const mins = Math.max(0, Math.round((now.getTime() - when.getTime()) / 60_000));
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
